import { useState, useEffect, useMemo } from 'react';

interface CoinGeckoData {
  id: string;
  name: string;
  symbol: string;
  image: string;
  current_price: number;
  market_cap: number;
  total_volume: number;
  price_change_percentage_24h: number;
}

const DATA_KEY = 'coingecko_prices_data';
const DATE_KEY = 'coingecko_prices_date';
const MAX_COINS = 1000; // Maximum number of coins to fetch
const COINS_PER_PAGE = 250; // CoinGecko typically allows 250 per page

export function useCoinGeckoPrices(currentPage: number, pageSize: number, maxCoins: number = MAX_COINS) {
  // Helper: read full data from local storage if valid for today.
  const getInitialPrices = (): CoinGeckoData[] => {
    // Check if we're in a browser environment before accessing localStorage
    if (typeof window === 'undefined') {
      return [];
    }
    
    try {
      const storedDataStr = localStorage.getItem(DATA_KEY);
      const storedDate = localStorage.getItem(DATE_KEY);
      const todayStr = new Date().toISOString().split('T')[0];
      if (storedDataStr && storedDate === todayStr) {
        return JSON.parse(storedDataStr);
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return [];
  };

  // fullPrices holds the complete set of coins
  const [fullPrices, setFullPrices] = useState<CoinGeckoData[]>(getInitialPrices());
  const [loading, setLoading] = useState(fullPrices.length === 0);
  const [totalCoins, setTotalCoins] = useState<number>(fullPrices.length);

  useEffect(() => {
    let isSubscribed = true;
    const todayStr = new Date().toISOString().split('T')[0];
    // If local data exists and is from today, no need to re-fetch.
    if (localStorage.getItem(DATE_KEY) === todayStr && fullPrices.length > 0) {
      setTotalCoins(fullPrices.length);
      return;
    }
    setLoading(true);
    
    async function fetchData() {
      try {
        const totalPages = Math.ceil(maxCoins / COINS_PER_PAGE);
        let allCoins: CoinGeckoData[] = [];
        
        // Make multiple API calls if needed to get more coins
        for (let page = 1; page <= totalPages; page++) {
          // Break if we've reached the maximum number of coins
          if (allCoins.length >= maxCoins) break;
          
          console.log(`Fetching CoinGecko data page ${page}/${totalPages}`);
          const response = await fetch(`/api/coingecko?page=${page}&per_page=${COINS_PER_PAGE}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to fetch data: ${errorData.message}`);
          }
          
          const data = await response.json();
          
          if (!Array.isArray(data) || data.length === 0) {
            // No more data or invalid response
            break;
          }
          
          allCoins = [...allCoins, ...data];
          
          // Add a small delay between requests to avoid rate limiting
          if (page < totalPages) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        if (isSubscribed && allCoins.length > 0) {
          setFullPrices(allCoins);
          setTotalCoins(allCoins.length);
          localStorage.setItem(DATA_KEY, JSON.stringify(allCoins));
          localStorage.setItem(DATE_KEY, todayStr);
        }
      } catch (error) {
        console.error('Error fetching CoinGecko data:', error);
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }
    
    fetchData();
    return () => {
      isSubscribed = false;
    };
  }, []); // Run only on mount

  // Return the paginated slice based on currentPage and pageSize.
  const paginatedPrices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return fullPrices.slice(startIndex, startIndex + pageSize);
  }, [fullPrices, currentPage, pageSize]);

  return { prices: paginatedPrices, totalCoins, loading };
}
